import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { CapacityPlanningActions } from './capacity-planning.actions';
import { convertListoToOptions } from '../../shared/ra.operations';
import { Campaign, CapacityPlanning, FilterStructure, LegalEntityCapacityPlanning } from '../../../../graphql-planner-tool/generated';

export const capacityPlanningsFeatureKey = 'capacityPlannings';

export interface State extends EntityState<LegalEntityCapacityPlanning> {
  loading: boolean;
  filters: any;
  totalElements: number;
  years: Date[];
  maxYear: number;
  minYear: number;
  campaigns: Campaign[];
  selectedCampaign: Campaign | null;
  allCapacityPlanning: LegalEntityCapacityPlanning[];
  filtersBE: FilterStructure[];
  loadingCampaign: boolean;
}

export const adapter: EntityAdapter<LegalEntityCapacityPlanning> = createEntityAdapter<LegalEntityCapacityPlanning>({
  selectId: (entity: LegalEntityCapacityPlanning) => entity?.id as string
});

export const initialState: State = adapter.getInitialState({
  loading: false,
  filters: {},
  years: [],
  maxYear: 0,
  totalElements: 0,
  campaigns: [],
  minYear: 0,
  selectedCampaign: null,
  allCapacityPlanning: [],
  filtersBE: [],
  loadingCampaign: false
});

export const reducer = createReducer(
  initialState,

  on(CapacityPlanningActions.loadCapacityPlanning, (state) => ({
    ...adapter.removeAll({ ...state, loading: true }),
    loading: true
  })),

  on(CapacityPlanningActions.loadCapacityPlanningSuccess, (state, action) => ({
    ...adapter.setAll(action.capacityPlanning.items as LegalEntityCapacityPlanning[], state),
    loading: false,
    filtersBE: action.capacityPlanning.filters as FilterStructure[],
    totalElements: action.capacityPlanning.pageInfo.totalItems as number
  })),

  on(CapacityPlanningActions.loadCapacityPlanningFailure, (state) => ({
    ...adapter.removeAll({ ...state, loading: false }),
    loading: false
  })),

  on(CapacityPlanningActions.setSelectedCampaign, (state, action) => ({
    ...state,
    selectedCampaign: action.campaign
  })),

  on(CapacityPlanningActions.loadCampaigns, (state) => ({
    ...state,
    campaigns: []
  })),

  on(CapacityPlanningActions.loadCampaignsSuccess, (state, action) => ({
    ...state,
    campaigns: action.campaigns?.items as Campaign[],
    maxYear: action?.campaigns?.items.length
      ? Math.max(...action.campaigns.items.map(c => c.year as number))
      : 0,
    minYear: action?.campaigns?.items.length
      ? Math.min(...action.campaigns.items.map(c => c.year as number))
      : 0
  })),

  on(CapacityPlanningActions.loadCampaignsFailure, (state) => ({
    ...state,
    campaigns: []
  })),

  on(CapacityPlanningActions.loadAllCapacityPlanning, (state) => ({
    ...state,
    allCapacityPlanning: []
  })),

  on(CapacityPlanningActions.loadAllCapacityPlanningSuccess, (state, action) => ({
    ...state,
    filters: buildGenericFilters(action.allCapacityPlanning.items as LegalEntityCapacityPlanning[]),
    allCapacityPlanning: action.allCapacityPlanning.items as LegalEntityCapacityPlanning[]
  })),

  on(CapacityPlanningActions.loadAllCapacityPlanningFailure, (state) => ({
    ...state,
    allCapacityPlanning: []
  })),

  on(CapacityPlanningActions.validateCapacityPlanning, (state) => ({
    ...state,
    loading: true
  })),

  on(CapacityPlanningActions.validateCapacityPlanningSuccess, (state, action) => ({
    ...adapter.updateOne({ id: action.capacityPlanning.data.id as string, changes: action.capacityPlanning.data }, state),
    loading: false
  })),

  on(CapacityPlanningActions.validateCapacityPlanningFailure, (state) => ({
    ...state,
    loading: false
  })),

  on(CapacityPlanningActions.createCampaigns, (state) => ({
    ...state,
    loading: true,
    loadingCampaign: true
  })),

  on(CapacityPlanningActions.createCampaignsSuccess, (state, action) => ({
    ...state,
    campaigns: [...state.campaigns, action.campaign.data as Campaign],
    selectedCampaign: action.campaign.data as Campaign,
    loading: false,
    loadingCampaign: false
  })),

  on(CapacityPlanningActions.createCampaignsFailure, (state) => ({
    ...state,
    loading: false,
    loadingCampaign: false
  })),

  on(CapacityPlanningActions.updateCampaigns, (state) => ({
    ...state,
    loading: true,
    loadingCampaign: true
  })),

  on(CapacityPlanningActions.updateCampaignsSuccess, (state, action) => ({
    ...state,
    campaigns: [...state.campaigns.map(c => c.id === action.campaign.data?.id ? action.campaign.data as Campaign : c)],
    selectedCampaign: (action.campaign.data?.id === state.selectedCampaign?.id ? action.campaign.data : state.selectedCampaign) as Campaign,
    loading: false,
    loadingCampaign: false
  })),

  on(CapacityPlanningActions.updateCampaignsFailure, (state) => ({
    ...state,
    loading: false,
    loadingCampaign: false
  })),

  on(CapacityPlanningActions.cleanReducer, (state) => ({
    ...adapter.removeAll(state),
    allCapacityPlanning: [],
    filters: {},
    totalElements: 0

  }))

);

export const capacityPlanningsFeature = createFeature({
  name: capacityPlanningsFeatureKey,
  reducer,
  extraSelectors: ({
    selectCapacityPlanningsState,
    selectEntities,
    selectIds,
    selectFilters
  }) => ({
    ...adapter.getSelectors(selectCapacityPlanningsState),

    selectCapacityPlanningList: createSelector(
      selectEntities,
      selectIds,
      (entities, ids) => {
        return ids?.map(id => entities?.[id] ?? []) || [] as CapacityPlanning[]
      }
    ),

    selectNames: (fieldName: string) =>
      createSelector(selectFilters, (filters) =>
        convertListoToOptions(filters?.[fieldName] || []))

  })
});

export const {
  selectIds,
  selectEntities,
  selectAll,
  selectTotal,
  selectFilters,
  selectMaxYear,
  selectLoading,
  selectYears,
  selectNames,
  selectMinYear,
  selectSelectedCampaign,
  selectCapacityPlanningList,
  selectTotalElements,
  selectCampaigns,
  selectFiltersBE,
  selectLoadingCampaign
} = capacityPlanningsFeature;


export function buildGenericFilters(valueChains: LegalEntityCapacityPlanning[]): Record<string, string[]> {
  const ret: Record<string, string[]> = {};

  function extract(obj: any, prefix = '') {
    for (const key in obj) {
      const value = obj[key];
      const path = prefix ? `${prefix}-${key}` : key; // concatena il path

      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        // ricorsione per oggetti annidati
        extract(value, path);
      } else {
        if (!ret[path]) ret[path] = [];
        ret[path].push(String(value));
      }
    }
  }

  for (const vc of valueChains) {
    extract(vc);
  }

  return ret;
}